import { useMutation } from '@apollo/client';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  AgendaAppointmentType,
  useAgendaAppointmentTypeValues,
} from '../../../../data/data/agenda/select-values';
import { useBulkTypeValues } from '../../../../data/data/bulk-messages/select-values';
import { useBulkMessageItem } from '../../../../data/data/bulk-messages/use-bulk-message-item';
import { useHealthProfessionalItems } from '../../../../data/data/health-professionals/use-health-professional-items';
import { useAvailableAppointmentHonorificBlockMinuteValues } from '../../../../data/data/honorific-block-times/use-available-appointment-honorific-block-minute-values';
import { toUpload } from '../../../../data/GraphQL/api/format';
import {
  BulkCampaignType,
  ConsultType,
  GetBulkItemsDocument,
  Honorific,
  MutateBulkMessageItemDocument,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import {
  FileItemData,
  FileSizeDocument,
  FileTypeImage,
  FileTypePdf,
  UploadFileItemData,
} from '../../../../data/utils/upload.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { FileUploadController } from '../../../../elements/file/controllers/file-upload-controller';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { InputTextAreaController } from '../../../../elements/form/controllers/input-textarea';
import { FormRow } from '../../../../elements/form/form';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Tooltip } from '../../../../elements/tooltip/tooltip';
import { InputHonorificBlockMinutesController } from '../../../form/controllers/input-honorific-block-minutes-controller/input-honorific-block-minutes-controller';
import { BulkMessageDeletePrompt } from '../bulk-message-delete-modal/bulk-message-delete-modal';
import { useBulkMessageForm } from './form/use-bulk-message-form';

export enum BulkMessageModalAction {
  DEFAULT = 'DEFAULT',
  UPDATE = 'UPDATE',
}

type BulkMessageModalProps = {
  id?: string | null;
  action?: BulkMessageModalAction;
  onSaved?: (id: string) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

function BulkMessageModal({
  id,
  onSaved,
  onCancel,
  onClose,
  ...props
}: BulkMessageModalProps) {
  const intl = useIntl();

  const { form, formIsValid } = useBulkMessageForm();
  const { showAlertMessage, healthProviderId } = useAppContext();

  const { data: item, loading: bulkItemLoading } = useBulkMessageItem(id);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const { data: bulkTypeOptions } = useBulkTypeValues();
  const [action, setAction] = useState<BulkMessageModalAction>(
    BulkMessageModalAction.DEFAULT
  );

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (action === BulkMessageModalAction.UPDATE) {
        if (
          !confirmed &&
          Object.values(form.formState.dirtyFields).some(
            value => value === true
          )
        ) {
          setCancelPromptOpen(true);
        } else if (id) {
          setAction(BulkMessageModalAction.DEFAULT);
        } else {
          onCancel?.();

          onClose?.();
        }
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [action, form.formState.dirtyFields, id, onCancel, onClose]
  );

  useEffect(() => {
    setAction(props.action ?? BulkMessageModalAction.DEFAULT);
  }, [props.action]);

  const type = form.watch('type');

  useEffect(() => {
    (async () => {
      if (bulkItemLoading) return;
      if (item) {
        form.setValue('internalTitle', item.internalTitle, {
          shouldDirty: false,
        });
        form.setValue('type', item.type, {
          shouldDirty: false,
        });
        form.setValue('titleNl', item.titleNl, {
          shouldDirty: false,
        });
        form.setValue('messageNl', item.messageNl, {
          shouldDirty: false,
        });
        form.setValue('titleEn', item.titleEn, {
          shouldDirty: false,
        });
        form.setValue('messageEn', item.messageEn, {
          shouldDirty: false,
        });
        form.setValue(
          'fileEn',
          item.fileEn
            ? ({
                fileName: item.fileEn?.fileName,
                originalFileName: item.fileEn?.originalFileName,
                size: item.fileEn?.size,
                thumbNail: item.fileEn?.thumbNail,
              } as UploadFileItemData)
            : null,
          {
            shouldDirty: false,
          }
        );
        form.setValue(
          'fileNl',
          item.fileNl
            ? ({
                fileName: item.fileNl?.fileName,
                originalFileName: item.fileNl?.originalFileName,
                size: item.fileNl?.size,
                thumbNail: item.fileNl?.thumbNail,
              } as UploadFileItemData)
            : null,
          {
            shouldDirty: false,
          }
        );
      }
    })();
  }, [bulkItemLoading, form, item]);

  const [mutateBulkMessageItem, { loading: mutateBulkMessageItemLoading }] =
    useMutation(MutateBulkMessageItemDocument, {
      refetchQueries: [GetBulkItemsDocument],
    });

  const onHandleFile = useCallback(
    (fieldName: 'fileNl' | 'fileEn', value: FileItemData) => {
      form.setValue(fieldName, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form]
  );

  // Schedule appointment

  const { getFullName, getFullNameAndHonorific } = useFormatters();

  const honorifics = form.watch('honorifics');
  const healthProfessionalId = form.watch('healthProfessionalId');
  const consultTypes = form.watch('consultTypes');

  const { data: appointmentTypeData } = useAgendaAppointmentTypeValues(
    AgendaAppointmentType.SIMPLE
  );

  useEffect(() => {
    form.setValue('healthProfessionalId', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form]);

  // reset healthproffesional if multiple honorifics are selected
  useEffect(() => {
    if (honorifics && honorifics?.length > 1 && healthProfessionalId) {
      form.setValue('healthProfessionalId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, healthProfessionalId, honorifics]);

  // reset honorifics if no consulttypes are selected
  useEffect(() => {
    if (
      !consultTypes ||
      (consultTypes.length === 0 && honorifics && honorifics.length > 0)
    ) {
      form.setValue('honorifics', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [consultTypes, form, honorifics]);

  const {
    data: availableAppointmentHonorificBlockMinuteData,
    loading: availableAppointmentHonorificBlockMinuteLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues({
    healthProviderId,
    internalAgenda: false,
    consultTypes: consultTypes as ConsultType[],
  });

  const { data: healthProfessionalItemsData } = useHealthProfessionalItems({
    healthProviderId,
    honorifics: honorifics ? (honorifics.map(x => x.key) as Honorific[]) : [],
    onlyAgenda: true,
  });

  // TODO COMBINE LOGIC WITH REMINDERS AND BULK
  // get availability of selected healthprofessional
  const {
    data: healthProfessionalAvailablilty,
    loading: healthProfessionalAvailabliltyLoading,
  } = useAvailableAppointmentHonorificBlockMinuteValues(
    {
      healthProviderId,
      internalAgenda: false,
      healthProfessionalId, // selected health professional
      consultTypes: consultTypes as ConsultType[],
    },
    healthProfessionalId === ''
  );

  const isSelectedHealthProfessionalAvailable = useMemo(() => {
    // No availability data - return false
    if (
      !healthProfessionalAvailablilty ||
      healthProfessionalAvailablilty.length === 0
    ) {
      return false;
    }

    // No honorifics selected - return false
    if (!honorifics || honorifics.length === 0) {
      return false;
    }

    // For each selected honorific, check if the health professional has available blocks
    return honorifics.some(selectedHonorific => {
      // Find matching honorific in availability data
      const availabilityForHonorific = healthProfessionalAvailablilty.find(
        availableItem => availableItem.key === selectedHonorific.key
      );

      if (!availabilityForHonorific?.values) {
        return false;
      }

      // Find the value matching the selected honorific value and check if it's available
      return availabilityForHonorific.values.some(
        timeSlot =>
          timeSlot.value === selectedHonorific.value &&
          timeSlot.available === true
      );
    });
  }, [honorifics, healthProfessionalAvailablilty]);

  const { healthProfessionalItem, healthProfessionalItems } = useMemo(
    () => ({
      healthProfessionalItem: (healthProfessionalItemsData?.items ?? []).find(
        option => option.id === healthProfessionalId
      ),
      healthProfessionalItems: healthProfessionalItemsData?.items ?? [],
    }),
    [healthProfessionalId, healthProfessionalItemsData]
  );

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await mutateBulkMessageItem({
          variables: {
            ...values,
            id: id ?? null,
            healthProviderId: healthProviderId ?? null,
            type: values.type,
            fileEn: values.fileEn
              ? {
                  fileName: values.fileEn?.fileName,
                  base64Data: toUpload(values.fileEn?.base64),
                }
              : {
                  deleteFile: true,
                },
            fileNl: values.fileNl
              ? {
                  fileName: values.fileNl?.fileName,
                  base64Data: toUpload(values.fileNl?.base64),
                }
              : {
                  deleteFile: true,
                },
            bulkPlanAppointment:
              type === BulkCampaignType.PlanAppointment
                ? {
                    healthProfessionalId: !isEmpty(values.healthProfessionalId)
                      ? values.healthProfessionalId
                      : null,
                    consultTypes: values.consultTypes as ConsultType[],
                    honorificBlockMinutes: values.honorifics
                      ? values.honorifics?.map(honorific => ({
                          blockMinutes: honorific.value,
                          honorific: honorific.key,
                        }))
                      : [],
                  }
                : undefined,
          },
        });

        if (result.data?.bulkCampaignMutate?.uuid) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'bulk-messages.bulk-message.success',
            }),
          });

          onSaved?.(result.data?.bulkCampaignMutate?.uuid);

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    healthProviderId,
    id,
    intl,
    mutateBulkMessageItem,
    onClose,
    onSaved,
    showAlertMessage,
    type,
  ]);

  return (
    <>
      <Modal
        title={<CustomFormattedMessage id="bulk-messages.bulk-message.title" />}
        width={ModalWidth.LARGE}
        onClose={() => {
          onHandleCancel(false);
        }}
        buttons={
          action === BulkMessageModalAction.UPDATE ? (
            <>
              <Button
                type={ButtonType.SECONDARY}
                inverted
                onClick={() => onHandleCancel(false)}
              >
                <CustomFormattedMessage id="common.cancel" />
              </Button>

              <Button
                icon={
                  bulkItemLoading || mutateBulkMessageItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={
                  !formIsValid ||
                  bulkItemLoading ||
                  mutateBulkMessageItemLoading
                }
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="bulk-messages.bulk-message.save" />
              </Button>
            </>
          ) : (
            <>
              {/* A campaign that reached a patient is only deactivated by the
                  server, so it offers "deactivate"; once inactive there is
                  nothing left to do. */}
              {id && item?.reachedPatients && item.active && (
                <Button
                  testId="bulk-message-deactivate"
                  icon={
                    bulkItemLoading || mutateBulkMessageItemLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={bulkItemLoading}
                  inverted
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <CustomFormattedMessage id="bulk-messages.bulk-message-deactivate.button" />
                </Button>
              )}
              {id && !item?.reachedPatients && (
                <Button
                  icon={
                    bulkItemLoading || mutateBulkMessageItemLoading
                      ? ButtonIcon.LOADING
                      : ButtonIcon.NONE
                  }
                  disabled={bulkItemLoading}
                  inverted
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <CustomFormattedMessage id="common.delete" />
                </Button>
              )}
              <Button
                disabled={bulkItemLoading}
                icon={
                  bulkItemLoading || mutateBulkMessageItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                onClick={() => setAction(BulkMessageModalAction.UPDATE)}
              >
                <CustomFormattedMessage id="bulk-messages.bulk-message.edit" />
              </Button>
            </>
          )
        }
      >
        {action === BulkMessageModalAction.UPDATE ? (
          <FormProvider {...form}>
            <FormRow>
              <InputFieldController
                name="internalTitle"
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.internal-title" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>
            <FormRow>
              <InputSelectController<BulkCampaignType>
                name="type"
                disabled={!isEmpty(id)}
                placeholder={intl.formatMessage({
                  id: 'common.bulk-type-placeholder',
                })}
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.bulk-type" />
                    <small>*</small>
                  </>
                }
                options={bulkTypeOptions}
              />
            </FormRow>

            <FormRow>
              <InputFieldController
                name="titleNl"
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.title-nl" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>
            <FormRow>
              <InputTextAreaController
                name="messageNl"
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.message-nl" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>

            <FormRow>
              <FileUploadController
                name="fileNl"
                /* label={
                  <>
                    <CustomFormattedMessage id="common.file" /> (NL)
                  </>
                } */
                fileTypes={[...FileTypeImage, ...FileTypePdf]}
                fileSize={FileSizeDocument}
                onChange={value =>
                  onHandleFile('fileNl', value as FileItemData)
                }
              />
            </FormRow>
            <FormRow>
              <InputFieldController
                name="titleEn"
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.title-en" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>
            <FormRow>
              <InputTextAreaController
                name="messageEn"
                label={
                  <>
                    <CustomFormattedMessage id="bulk-messages.bulk-message.message-en" />
                    <small>*</small>
                  </>
                }
              />
            </FormRow>
            <FormRow>
              <FileUploadController
                name="fileEn"
                /* label={
                  <>
                    <CustomFormattedMessage id="common.file" /> (EN)
                  </>
                } */
                fileTypes={[...FileTypeImage, ...FileTypePdf]}
                fileSize={FileSizeDocument}
                onChange={value =>
                  onHandleFile('fileEn', value as FileItemData)
                }
              />
            </FormRow>

            {type === BulkCampaignType.PlanAppointment && (
              <>
                <FormRow>
                  <InputCheckboxController<ConsultType>
                    name="consultTypes"
                    label={
                      <>
                        <CustomFormattedMessage id="common.consult-types" />
                        <small>*</small>
                      </>
                    }
                    options={appointmentTypeData}
                    multiple
                  />
                </FormRow>

                <FormRow>
                  {!availableAppointmentHonorificBlockMinuteLoading &&
                    consultTypes.length > 0 && (
                      <InputHonorificBlockMinutesController
                        label={
                          <>
                            <CustomFormattedMessage id="common.honorific" />
                            <small>*</small>
                          </>
                        }
                        name="honorifics"
                        options={availableAppointmentHonorificBlockMinuteData}
                      />
                    )}
                  {availableAppointmentHonorificBlockMinuteLoading && (
                    <CustomFormattedMessage id="common.loading" />
                  )}
                  {consultTypes.length === 0 && (
                    <CustomFormattedMessage id="agenda.appointment-create.no-consult-type" />
                  )}
                </FormRow>

                <FormRow>
                  <InputSelectController<string>
                    className="chat-message-health-professional"
                    name="healthProfessionalId"
                    label={
                      <>
                        {!healthProfessionalAvailabliltyLoading &&
                          healthProfessionalId &&
                          isSelectedHealthProfessionalAvailable === false && (
                            <Tooltip
                              message={
                                <CustomFormattedMessage id="common.honorific-block-minutes-not-available" />
                              }
                            >
                              <i className="icon info" />
                            </Tooltip>
                          )}
                        <CustomFormattedMessage id="common.health-professional" />
                      </>
                    }
                    placeholder={intl.formatMessage({
                      id: 'common.health-professional-placeholder',
                    })}
                    value={getFullName(healthProfessionalItem)}
                    options={healthProfessionalItems.map(option => ({
                      key: option.id ?? '',
                      title: getFullNameAndHonorific(option),
                    }))}
                    disabled={!honorifics?.length || honorifics?.length > 1}
                  />
                </FormRow>
              </>
            )}
          </FormProvider>
        ) : (
          <div className="bulk-message-modal-info">
            {bulkItemLoading ? (
              <p>
                <CustomFormattedMessage id="common.loading" />
              </p>
            ) : (
              <div className="bulk-message-modal-info__content">
                <h4>
                  <CustomFormattedMessage id="common.title" />
                </h4>
                <p>{item?.internalTitle ?? ''}</p>
                <h4>
                  <CustomFormattedMessage id="bulk-messages.bulk-message.bulk-type" />
                </h4>
                <p>
                  {bulkTypeOptions.find(x => x.key === item?.type)?.title ?? ''}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
      {deleteModalOpen && (
        <BulkMessageDeletePrompt
          item={item}
          onDeleted={() => {
            onClose?.();
          }}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}
    </>
  );
}

export { BulkMessageModal };
